// Read-only detail view for a deliverable / obligation. Renders through
// the shared TrackingDetailPanel: the derived status + effective RAG
// pills (or the recurring-schedule chip on a parent) compose into the
// panel's badges slot, the Re-open / Edit buttons into its actions slot,
// the deliverable fields (incl. the per-stage lifecycle stamps with
// comments) into its <dl>, and the lifecycle timeline + evidence links +
// notes into its children slot. Also used internally by
// DeliverableDetailModal (archive deep-link).
//
// The RAG badge uses deriveEffectiveRag (not the stored ragRating) so an
// overdue, non-terminal deliverable shows Red here — matching the table
// row — instead of whatever the user last graded.

import { Pencil, ExternalLink, Undo2 } from "lucide-react";
import Button from "../../../common/Button";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import OpenIssueCountBadge from "../shared/OpenIssueCountBadge";
import RaiseIssueButton from "../shared/RaiseIssueButton";
import TrackingDetailPanel, {
  type TrackingDetailField,
} from "../shared/TrackingDetailPanel";
import type {
  Deliverable,
  DeliverableDerivedStatus,
  Milestone,
} from "../../../../types";
import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import { deriveEffectiveRag } from "./deriveEffectiveRag";
import { deriveMilestoneLifecycleStatus } from "../../../../utils/deriveMilestoneStatus";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import { safeExternalHref } from "../../../../utils/evidenceLinks";
import DeliverableLifecycleTimeline from "./DeliverableLifecycleTimeline";

interface DeliverableDetailExpandProps {
  item: Deliverable;
  /** Milestone lookup for resolving milestoneId → display name + status.
   *  Optional — detail modal context may not have the milestone list. */
  milestones?: Milestone[];
  /** When the expanded item is a recurring occurrence (child row), the
   *  caller supplies the parent schedule so the header can disambiguate
   *  via the occurrence period. Omit for one-off / parent / orphan
   *  rows — they keep the legacy single-line header. */
  parent?: Deliverable;
  /** Short period label for a recurring occurrence (e.g. "Apr 2026").
   *  When supplied, appended to the header as
   *  `{parent name} — {occurrenceTitle}`. */
  occurrenceTitle?: string;
  /** Anchor date for status + effective-RAG derivation. Defaults to now;
   *  tests inject a frozen date so the overdue → Red rule is deterministic. */
  today?: Date;
  onEdit?: () => void;
  /** Fired when the user clicks the linked-milestone pill. Parent wires
   *  this to a URL mutation that switches to the milestone tab and
   *  expands the row. Omit to render the pill as a disabled label
   *  (modal contexts where navigation isn't wired). */
  onNavigateToMilestone?: (milestoneId: string) => void;
  /** Fired when the user clicks the Re-open button on a terminal-state
   *  deliverable. Parent wires this to open the AcceptanceGateDialog
   *  in reopen-only mode (the dialog auto-detects terminal status and
   *  renders only the Re-open affordance). Omit in modal contexts that
   *  don't manage the gate dialog — the button is hidden when no
   *  callback is supplied. */
  onReopenRequested?: () => void;
}

const TYPE_LABELS: Record<Deliverable["deliverableType"], string> = {
  deliverable: "Deliverable",
  obligation: "Obligation",
};

const OWNER_LABELS: Record<Deliverable["owner"], string> = {
  supplier: "Supplier",
  customer: "Customer",
  joint: "Joint",
};

const FREQUENCY_LABELS: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

function describeRecurrence(
  rule: Deliverable["recurrenceRule"],
): string | null {
  if (!rule) return null;
  const base = FREQUENCY_LABELS[rule.frequency] ?? rule.frequency;
  const interval =
    rule.interval && rule.interval > 1 ? ` × ${rule.interval}` : "";
  const range = `from ${formatDayMonthYear(rule.startDate)}${rule.endDate ? ` until ${formatDayMonthYear(rule.endDate)}` : ""}`;
  return `${base}${interval}, ${range}`;
}

export default function DeliverableDetailExpand({
  item,
  milestones,
  parent,
  occurrenceTitle,
  today,
  onEdit,
  onNavigateToMilestone,
  onReopenRequested,
}: DeliverableDetailExpandProps) {
  const now = today ?? new Date();
  const isRecurringParent = !!item.recurrenceRule;
  const derivedStatus: DeliverableDerivedStatus = deriveDeliverableStatus(
    item.status,
    item.dueDate,
    now,
    item.submittedAt,
  );
  // Effective RAG layers the overdue → Red / accepted-no-RAG → Green soft
  // defaults over the stored value, so the badge tells the same story as
  // the table row instead of showing a stale stored amber/green on an
  // overdue item.
  const effectiveRag = deriveEffectiveRag(item, now);
  const linkedMilestone = item.milestoneId
    ? milestones?.find((m) => m.id === item.milestoneId)
    : undefined;
  const milestoneName = item.milestoneId
    ? (linkedMilestone?.name ?? item.milestoneId)
    : null;
  const recurrence = describeRecurrence(item.recurrenceRule);

  // Per-occurrence header: when the caller supplies the parent + a
  // short period title (e.g. "Apr 2026"), the heading reads
  // "Monthly Service Report — Apr 2026" so users can tell which
  // occurrence they're inspecting at a glance. Falls back to the raw
  // item.name for one-offs, orphans, and parent rows.
  const headerTitle =
    parent && occurrenceTitle
      ? `${parent.name} — ${occurrenceTitle}`
      : item.name;

  const fields: TrackingDetailField[] = [
    { label: "Type", value: TYPE_LABELS[item.deliverableType] },
    { label: "Owner", value: OWNER_LABELS[item.owner] },
  ];
  // Due date, milestone link, and per-row status only apply to non-
  // recurring items — a recurring parent's due date is just a Phase-2
  // schema artefact and doesn't represent a real compliance date.
  if (!isRecurringParent && item.dueDate) {
    fields.push({ label: "Due date", value: formatDayMonthYear(item.dueDate) });
  }
  if (item.clauseReference) {
    fields.push({ label: "Clause reference", value: item.clauseReference });
  }
  if (!isRecurringParent && milestoneName && item.milestoneId) {
    fields.push({
      label: "Linked milestone",
      value: (
        <button
          type="button"
          onClick={() => onNavigateToMilestone?.(item.milestoneId!)}
          disabled={!onNavigateToMilestone}
          data-testid={`deliverable-detail-linked-milestone-${item.milestoneId}`}
          aria-label={`View milestone ${milestoneName}`}
          className="group inline-flex items-center gap-2 rounded-sm py-0.5 text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 disabled:cursor-default"
        >
          {/* The milestone-name span carries the affordance — brand text
           *  + hover underline reads as a link in both light and dark
           *  modes, where the previous slate-50 hover background was
           *  invisible on dark surfaces. Disabled state (modal contexts
           *  with no nav callback) drops the link styling so the pill
           *  reads as inert. */}
          <span className="text-body font-medium text-brand-700 group-hover:underline group-disabled:text-slate-500 group-disabled:no-underline dark:text-brand-300 dark:group-disabled:text-slate-400">
            {milestoneName}
          </span>
          {linkedMilestone && (
            <TrackingStatusBadge
              kind="milestone"
              status={deriveMilestoneLifecycleStatus(linkedMilestone)}
            />
          )}
          {onNavigateToMilestone && (
            <ExternalLink
              className="h-3.5 w-3.5 text-brand-700 dark:text-brand-300"
              aria-hidden="true"
            />
          )}
        </button>
      ),
    });
  }
  if (recurrence) {
    fields.push({ label: "Recurrence", value: recurrence });
  }
  // Lifecycle stamps with per-stage comments. Each transition
  // (submit / accept / reject) records both a timestamp and an optional
  // free-text note captured by the matching modal.
  if (!isRecurringParent && item.submittedAt) {
    fields.push({
      label: "Submitted",
      value: formatDayMonthYear(item.submittedAt),
      comment: item.submittedComment,
      commentTestId: "deliverable-submitted-comment",
    });
  }
  if (!isRecurringParent && item.acceptedAt) {
    fields.push({
      label: "Accepted",
      value: formatDayMonthYear(item.acceptedAt),
      comment: item.acceptedComment,
      commentTestId: "deliverable-accepted-comment",
    });
  }
  if (!isRecurringParent && item.rejectedAt) {
    fields.push({
      label: "Rejected",
      value: formatDayMonthYear(item.rejectedAt),
      comment: item.rejectedComment,
      commentTestId: "deliverable-rejected-comment",
    });
  }

  const showReopen =
    onReopenRequested &&
    !isRecurringParent &&
    (derivedStatus === "accepted" ||
      derivedStatus === "rejected" ||
      derivedStatus === "closed");

  return (
    <TrackingDetailPanel
      testId="deliverable-detail-expand"
      title={headerTitle}
      badges={
        isRecurringParent ? (
          <span
            className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-caption font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300"
            title="This is the recurring schedule, not a single occurrence."
          >
            Recurring schedule
          </span>
        ) : (
          <>
            <TrackingStatusBadge kind="deliverable" status={derivedStatus} />
            {effectiveRag && (
              <TrackingStatusBadge kind="rag" rating={effectiveRag} />
            )}
            <OpenIssueCountBadge
              contractId={item.contractId}
              targetType="deliverable"
              targetId={item.id}
            />
          </>
        )
      }
      description={item.description}
      fields={fields}
      actions={
        <>
          {/* Terminal-state deliverables get a Re-open affordance that
           *  opens AcceptanceGateDialog in reopen-only mode. The dialog
           *  records a `reverted` lifecycleEvent on confirm, preserving
           *  the audit trail of the prior terminal state. */}
          {showReopen && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Undo2 className="h-4 w-4" />}
              onClick={onReopenRequested}
              data-testid="deliverable-detail-reopen"
            >
              Re-open
            </Button>
          )}
          {/* Failing-signal raise: rejected deliverables can spawn a tracked
           *  issue (auto-linked to this row) so the contract manager doesn't
           *  re-enter the same context in the Issues tab. */}
          {!isRecurringParent && derivedStatus === "rejected" && (
            <RaiseIssueButton
              contractId={item.contractId}
              srcType="deliverable"
              srcId={item.id}
              srcTitle={item.name}
            />
          )}
          {onEdit && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil className="h-4 w-4" />}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
        </>
      }
    >
      {/* Recurring parent schedules have no per-occurrence lifecycle — only
       *  individual occurrences carry events. */}
      {!isRecurringParent && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Lifecycle history
          </h4>
          <div className="mt-2">
            <DeliverableLifecycleTimeline
              lifecycleEvents={item.lifecycleEvents}
            />
          </div>
        </div>
      )}

      {!isRecurringParent &&
        item.evidenceLinks &&
        item.evidenceLinks.length > 0 && (
          <div>
            <h4 className="text-caption text-slate-500 dark:text-slate-400">
              Evidence links
            </h4>
            <ul className="mt-2 space-y-1">
              {item.evidenceLinks.map((link, i) => {
                const href = safeExternalHref(link.url);
                const text = link.label || link.url;
                return (
                  <li key={i}>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-body text-brand-600 hover:text-brand-500 dark:text-brand-400 hover:underline"
                      >
                        {text}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span className="text-body text-slate-700 dark:text-slate-300">
                        {text}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

      {item.notes && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Notes
          </h4>
          <p className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100">
            {item.notes}
          </p>
        </div>
      )}
    </TrackingDetailPanel>
  );
}
